import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { notificationEndpoints } from "@/features/notification/api/endpoints";
import type {
  SimulateEventResponse,
  SimulateKycCaseExpiredRequest,
} from "@/features/notification/api/dto";

/**
 * Déclenche la notification d'expiration d'un dossier KYC (invite au
 * renouvellement) (`POST /NotificationEvents/simulate-kyc-case-expired` —
 * guide §6.7). Rôles : flashpay-superadmin, flashpay-service.
 */
export const useSimulateKycCaseExpired = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["notification", "events", "simulate-kyc-case-expired"],
    mutationFn: (body: SimulateKycCaseExpiredRequest) =>
      mutateData({
        url: notificationEndpoints.events.simulateKycCaseExpired,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<SimulateEventResponse>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        [["NOTIFICATION_LOGS_LIST"]],
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSimulateKycCaseExpired failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "notification" }
      );
    },
  });
};
